import React, { useEffect } from 'react';
import { MessageHostDiagram } from './MessageHostDiagram';

const font = "'Inria Serif', serif";

/**
 * Full-screen coach modal that appears right when the host taps "Share invite"
 * but BEFORE we open the Airbnb listing. Reminds the host where to find the
 * easy-to-miss "Message host" button. One CTA actually opens the Airbnb app.
 */
export const MessageHostCoachModal = ({ open, onConfirm, onCancel }) => {
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') onCancel?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      data-testid="message-host-coach-modal"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(8, 14, 22, 0.72)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '14px 14px 0',
      }}
    >
      {/* Card */}
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 460,
          background: 'var(--surface)',
          borderRadius: 24,
          padding: '18px 18px 22px',
          display: 'grid',
          gap: 14,
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ width: 44, height: 5, borderRadius: 9999, background: 'var(--border, #E5E5EC)', margin: '0 auto' }} />

        <p
          data-testid="message-host-coach-title"
          style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0, textAlign: 'center', lineHeight: 1.15 }}
        >
          Where to tap inside Airbnb
        </p>

        <p
          data-testid="message-host-coach-subtitle"
          style={{ fontFamily: font, fontSize: 15, color: 'var(--text-2)', margin: 0, textAlign: 'center', lineHeight: 1.35 }}
        >
          When Airbnb opens, scroll past the map and host details — then tap{' '}
          <strong>"Message host"</strong> and paste.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 2px' }}>
          <MessageHostDiagram width={240} />
        </div>

        <ol
          style={{
            fontFamily: font,
            fontSize: 14,
            color: 'var(--text)',
            margin: '0 4px',
            paddingLeft: 18,
            display: 'grid',
            gap: 4,
            lineHeight: 1.3,
          }}
        >
          <li>Scroll below the map</li>
          <li>Pass "Meet your host" and "Host details"</li>
          <li>Tap the grey <strong>"Message host"</strong> pill</li>
          <li>Long-press the message field → <strong>Paste</strong> → Send</li>
        </ol>

        <button
          type="button"
          data-testid="message-host-coach-open-button"
          onClick={onConfirm}
          style={{
            width: '100%',
            border: 'none',
            borderRadius: 9999,
            padding: '14px 20px',
            background: '#04A628',
            color: '#FFFFFF',
            fontFamily: font,
            fontSize: 20,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 10px 24px rgba(4, 166, 40, 0.25)',
          }}
        >
          Copy And Share
        </button>
      </div>

      {/* Cancel — lives outside the card so it always sits above the system nav bar */}
      <button
        type="button"
        data-testid="message-host-coach-cancel-button"
        onClick={onCancel}
        style={{
          width: '100%',
          maxWidth: 460,
          border: 'none',
          background: 'transparent',
          color: '#FFFFFF',
          fontFamily: font,
          fontSize: 15,
          fontWeight: 600,
          cursor: 'pointer',
          padding: '14px 0',
          paddingBottom: 'max(14px, env(safe-area-inset-bottom, 14px))',
        }}
      >
        Cancel
      </button>
    </div>
  );
};

export default MessageHostCoachModal;
